import { MySection } from '@/components/custom/my-section';
import { FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FulfillmentCarrier, FulfillmentMethod } from '@/types/fulfillment-type';
import { OrderCreateFormValues } from '@/types/order-type';
import { Stack } from '@mui/material';
import { useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';

interface FulfillmentSectionProps {
  form: UseFormReturn<OrderCreateFormValues>;
}

const fulfillmentMethodOptions = [
  { value: FulfillmentMethod.DELIVERY_CITY, label: 'Delivery City' },
  { value: FulfillmentMethod.DELIVERY_PROVINCE, label: 'Delivery Province' },
  { value: FulfillmentMethod.PICKUP, label: 'Pickup' },
];

const fulfillmentCarrierOptions = [
  { value: FulfillmentCarrier.VIREAK_BUNTHAM, label: 'Vireak Buntham' },
  { value: FulfillmentCarrier.JNT, label: 'J&T' },
];

export function FulfillmentSection({ form }: FulfillmentSectionProps) {
  // Set default values on mount if not already set
  useEffect(() => {
    const currentMethod = form.getValues('fulfillment.method');
    const currentCarrier = form.getValues('fulfillment.carrier');
    const currentPrice = form.getValues('fulfillment.price');

    let methodToUse = currentMethod;
    if (!currentMethod) {
      methodToUse = FulfillmentMethod.DELIVERY_CITY;
      form.setValue('fulfillment.method', methodToUse);
    }

    if (methodToUse !== FulfillmentMethod.PICKUP) {
      if (!currentCarrier) {
        form.setValue('fulfillment.carrier', FulfillmentCarrier.VIREAK_BUNTHAM);
      }
      if (!currentPrice || currentPrice === '') {
        form.setValue('fulfillment.price', '2');
      }
    }
  }, [form]);

  const fulfillmentMethod = form.watch('fulfillment.method');
  const isDelivery =
    fulfillmentMethod === FulfillmentMethod.DELIVERY_CITY ||
    fulfillmentMethod === FulfillmentMethod.DELIVERY_PROVINCE;

  return (
    <MySection title="Delivery Options">
      <Stack spacing={3}>
        <FormField
          control={form.control}
          name="fulfillment.method"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Select
                  onValueChange={(value) => {
                    field.onChange(value);
                    // Clear carrier and price when method changes to PICKUP
                    if (value === FulfillmentMethod.PICKUP) {
                      form.setValue('fulfillment.carrier', undefined);
                      form.setValue('fulfillment.price', undefined);
                    } else {
                      // Set default carrier and price when switching to delivery methods if they're empty
                      const currentCarrier = form.getValues('fulfillment.carrier');
                      const currentPrice = form.getValues('fulfillment.price');
                      if (!currentCarrier) {
                        form.setValue('fulfillment.carrier', FulfillmentCarrier.VIREAK_BUNTHAM);
                      }
                      if (!currentPrice || currentPrice === '') {
                        form.setValue('fulfillment.price', '2');
                      }
                    }
                  }}
                  value={field.value || undefined}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select fulfillment method" />
                  </SelectTrigger>
                  <SelectContent>
                    {fulfillmentMethodOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {isDelivery && (
          <>
            <FormField
              control={form.control}
              name="fulfillment.carrier"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Select
                      onValueChange={(value) => field.onChange(value)}
                      value={field.value || undefined}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select carrier" />
                      </SelectTrigger>
                      <SelectContent>
                        {fulfillmentCarrierOptions.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex mt-1">
              {/* $ prefix */}
              <span
                className="inline-flex items-center px-3 border border-gray-300 bg-gray-50 text-gray-500 
                   rounded-l-md rounded-r-none"
              >
                $
              </span>

              {/* Input */}
              <FormField
                control={form.control}
                name="fulfillment.price"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormControl>
                      <Input
                        type="text"
                        placeholder="2.00"
                        value={field.value ?? ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || /^\d+(\.\d{0,2})?$/.test(value)) {
                            field.onChange(value);
                          }
                        }}
                        className="
              rounded-l-none        /* removes left rounding */
              border-l-0            /* removes left border */
              border-gray-300
            "
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </>
        )}
      </Stack>
    </MySection>
  );
}
